import {INoticesDomainStore} from "../../domainStore/pages/noticesDomainStore";
import {action, makeObservable, observable, runInAction} from "mobx";
import {IModalStore} from "../global/modalStore";
import {debounce} from "lodash";
import {SortOrder} from "../../../shared/enums/sortOrder";
import {ObservableList} from "../../observables/observableList";
import {KeyValue} from "../../observables/keyValue";

/**
 * The interface for notice UI store.
 */
export interface INoticesUiStore {
  /**
   * Indicating that the notice UI is navigating.
   */
  isNavigating: boolean;
  /**
   * Indicating that the notice list is loading.
   */
  isListLoading: boolean;
  /**
   * Indicating that an editable notice is loading.
   * */
  isEditLoading: boolean;
  /**
   * If selecting edit from list instead of detail modal, the ui acts differently.
   * */
  editFromList: boolean;
  /**
   * Indicating that the notice modal is loading.
   */
  isModalLoading: boolean;
  /**
   * Indicating that the notice is in the create or edit state.
   */
  isCreatingOrEditing: boolean;
  /**
   * First load has occurred, safe to perform a second load.
   */
  hasPerformedFirstLoad: boolean;
  /**
   * The total number of notices in the data store.
   */
  totalNotices: number;
  /**
   * The event handler for notice list page navigation.
   */
  navigateNoticesPage: (
    pageNumber: number,
    sizePerPage: number,
    sortField?: string,
    sortOrder?: SortOrder
  ) => Promise<void>;
  /**
   * The event handler for select editable notice from the list.
   */
  selectEditableNotice: (noticeId: string) => Promise<void>;
  reloadEditableNotice: (noticeId: string) => Promise<void>;

  enableEditFromList: () => void;

  currentNoticeListPageNumber: number;
  currentNoticeListSizePerPage: number;
  currentNoticeListSortField: string | undefined;

  currentNoticeListSortOrder: SortOrder | undefined;
  onSelectedVillageChange: () => Promise<void>;
  selectableVillages?: ObservableList<KeyValue>;

  resetPaginationSettings: () => void;
  resetIsCreatingOrEditingFlag: () => void;
  isViewingNotice: boolean;
  resetIsViewingNoticeFlag: () => void;
}

/**
 * The UI store for notices.
 */
export class NoticesUiStore implements INoticesUiStore {
  private readonly defaultNoticeListPageNumber = 1;
  private readonly defaultNoticeListSizePerPage = 18;
  private readonly defaultNoticeListSortField = "status";
  private readonly defaultNoticeListSortOrder = SortOrder.Ascending;
  private noticesDomainStore: INoticesDomainStore;
  private modalStore: IModalStore;
  isNavigating: boolean;
  isListLoading: boolean;
  isEditLoading: boolean;
  editFromList: boolean;
  isModalLoading: boolean;
  hasPerformedFirstLoad: boolean;
  isCreatingOrEditing: boolean = false;
  totalNotices: number;
  currentNoticeListPageNumber: number = this.defaultNoticeListPageNumber;
  currentNoticeListSizePerPage: number = this.defaultNoticeListSizePerPage;
  currentNoticeListSortField: string | undefined =
    this.defaultNoticeListSortField;
  currentNoticeListSortOrder: SortOrder | undefined =
    this.defaultNoticeListSortOrder;
  selectableVillages: ObservableList<KeyValue>;
  isViewingNotice: boolean = false;
  /**
   * Callers awaiting the in-flight debounced page load. All resolved together
   * once the debounced fetch actually runs, so no caller sees loading flags
   * clear before the real data has arrived.
   */
  private pendingNavigationResolvers: Array<() => void> = [];
  private navigationRequestId = 0;

  constructor(
    noticesDomainStore: INoticesDomainStore,
    modalStore: IModalStore,
    selectableVillages: ObservableList<KeyValue>
  ) {
    makeObservable(this, {
      isNavigating: observable,
      isListLoading: observable,
      isEditLoading: observable,
      editFromList: observable,
      isModalLoading: observable,
      isCreatingOrEditing: observable,
      totalNotices: observable,
      navigateNoticesPage: action,
      isViewingNotice: observable
    });
    this.isNavigating = false;
    this.isListLoading = false;
    this.isEditLoading = false;
    this.editFromList = false;
    this.isModalLoading = false;
    this.hasPerformedFirstLoad = false;
    this.totalNotices = 0;
    this.noticesDomainStore = noticesDomainStore;
    this.modalStore = modalStore;
    this.selectableVillages = selectableVillages;
  }

  onSelectedVillageChange = async () => {
    if (!this.hasPerformedFirstLoad && !this.isNavigating) {
      return;
    }
    runInAction(() => {
      this.isListLoading = true;
      // Clear the previous village's notices immediately so the list visibly
      // empties out rather than continuing to show stale data while the new
      // village's notices are fetched.
      this.noticesDomainStore.noticesList = [];
      this.totalNotices = 0;
    });
    this.noticesDomainStore.selectedVillageId =
      this.selectableVillages.selectedItem?.value;
    await this.navigateNoticesPage(
      this.defaultNoticeListPageNumber,
      this.currentNoticeListSizePerPage,
      this.defaultNoticeListSortField,
      this.defaultNoticeListSortOrder
    );
  };

  resetIsViewingNoticeFlag = () => {
    runInAction(() => (this.isViewingNotice = false));
  };

  /**
   * A single, instance-scoped debounced fetcher. Reusing the same debounced
   * function (instead of creating a new one on every call) makes rapid
   * successive calls actually collapse into a single fetch, and lets
   * navigateNoticesPage await the real completion below.
   */
  private debouncedLoadNotices = debounce(
    async (
      pageNumber: number,
      sizePerPage: number,
      sortField: string | undefined,
      sortOrder: SortOrder | undefined,
      requestId: number,
      villageId: string
    ) => {
      if (requestId === this.navigationRequestId) {
        runInAction(() => {
          this.isListLoading = true;
        });
      }
      try {
        const totalNotices = await this.noticesDomainStore.loadNoticeArticles(
          villageId,
          pageNumber,
          sizePerPage,
          sortField ?? this.currentNoticeListSortField,
          sortOrder ?? this.currentNoticeListSortOrder
        );
        if (requestId === this.navigationRequestId) {
          runInAction(() => {
            this.totalNotices = totalNotices;
          });
        }
      } catch (error) {
        if (requestId === this.navigationRequestId) {
          this.modalStore.openErrorModal(error as Error);
        }
      }
      if (requestId === this.navigationRequestId) {
        runInAction(() => {
          this.isListLoading = false;
          this.isNavigating = false;
          this.hasPerformedFirstLoad = true;
        });
        this.resolvePendingNavigations();
      }
    },
    300
  );

  private resolvePendingNavigations() {
    const resolvers = this.pendingNavigationResolvers;
    this.pendingNavigationResolvers = [];
    resolvers.forEach((resolve) => resolve());
  }

  navigateNoticesPage = async (
    pageNumber: number,
    sizePerPage: number,
    sortField?: string,
    sortOrder?: SortOrder
  ) => {
    const villageId = this.selectableVillages.selectedItem?.value;
    const requestId = ++this.navigationRequestId;
    this.noticesDomainStore.invalidateNoticeArticleRequests();
    if (!villageId) {
      this.debouncedLoadNotices.cancel();
      runInAction(() => {
        this.isListLoading = false;
        this.isNavigating = false;
      });
      this.resolvePendingNavigations();
      return;
    }
    // When updating observables in async, we need to mark them as action. Otherwise, warnings will be shown in the browser's developer console.
    // https://mobx.js.org/actions.html#asynchronous-actions
    runInAction(() => {
      this.currentNoticeListPageNumber = pageNumber;
      this.currentNoticeListSizePerPage = sizePerPage;
      this.currentNoticeListSortField =
        sortField ?? this.currentNoticeListSortField;
      this.currentNoticeListSortOrder =
        sortOrder ?? this.currentNoticeListSortOrder;
      this.isNavigating = true;
    });
    // Wait for the debounced fetch to actually run and complete, so
    // isListLoading/isNavigating (and stale data on screen) only clear once
    // the new village's data has really loaded.
    await new Promise<void>((resolve) => {
      this.pendingNavigationResolvers.push(resolve);
      this.debouncedLoadNotices(
        pageNumber,
        sizePerPage,
        sortField,
        sortOrder,
        requestId,
        villageId
      );
    });
  };

  selectEditableNotice = async (noticeId: string) => {
    runInAction(() => {
      this.isModalLoading = true;
      this.isEditLoading = true;
    });
    try {
      await this.noticesDomainStore.loadEditableNotice(noticeId);
    } catch (_) {}
    runInAction(() => {
      this.isModalLoading = false;
      this.isEditLoading = false;
    });
  };

  reloadEditableNotice = async (noticeId: string) => {
    //waiting 10ms for loading visuals to catch up
    setTimeout(async () => {
      runInAction(() => {
        this.isEditLoading = true;
      });
      try {
        await this.noticesDomainStore.loadEditableNotice(noticeId);
      } catch (error) {
        this.modalStore.openErrorModal(error as Error);
      }
      runInAction(() => {
        this.isEditLoading = false;
        this.editFromList = false;
      });
    }, 10);
  };

  enableEditFromList = () => {
    this.editFromList = true;
    this.noticesDomainStore.resetEditableNotice();
  };

  resetPaginationSettings = () => {
    this.currentNoticeListPageNumber = this.defaultNoticeListPageNumber;
    this.currentNoticeListSizePerPage = this.defaultNoticeListSizePerPage;
    this.currentNoticeListSortField = this.defaultNoticeListSortField;
    this.currentNoticeListSortOrder = this.defaultNoticeListSortOrder;
  };

  resetIsCreatingOrEditingFlag = () => {
    runInAction(() => {
      this.isCreatingOrEditing = false;
    });
  };
}
